import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import Board from '../components/game/Board';
import useSolutionPlayback from '../hooks/useSolutionPlayback';
import { buildFrames } from '../solutionPlayback';
import { SCENES } from '../tutorial/scenes';
import { markIntroSeen } from '../firstVisit';
import scrollbar from '../styles/scrollbar';
import LanguagePicker from '../components/LanguagePicker';
import { useT } from '../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// Slower than the in-game playback. This one is being read, not raced.
const STEP_MS = 900;

const useStyles = createUseStyles({
  // Bounded to the viewport rather than `minHeight`, because `index.css` pins
  // `html { overflow: hidden }` for the game board's sake: a page that grows
  // past the fold here does not become scrollable, it becomes unreachable.
  // Same shape as Game.js's wrapper — own the viewport, scroll inside it.
  page: {
    height: '100dvh',
    fallbacks: [{ height: '100vh' }],
    background: 'linear-gradient(160deg, #1a0f08 0%, #241606 60%, #1a0f08 100%)',
    color: '#f5e6c8',
    // No bottom padding: the control bar carries its own, plus the safe area.
    padding: '18px 16px 0',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // The only scrolling region on the page, and it holds only what can afford
  // to scroll — the board and its caption. `minHeight: 0` is what lets a flex
  // child shrink below its content and actually scroll; without it the region
  // grows to fit instead and pushes the control bar back off the bottom.
  //
  // Centring is done with auto margins on the child rather than `alignItems`,
  // which overflows a scroll container in both directions and puts the top of
  // a tall board out of reach above the scrollport.
  main: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    width: '100%',
    ...scrollbar,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
    maxWidth: '1120px',
    margin: '0 auto 18px',
  },
  // In the header, not the control bar: that bar is a non-scrolling sibling of
  // `main` by design and has no room to spare.
  headerLanguage: {
    minWidth: 0,
    maxWidth: '260px',
  },
  backLink: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: '#d99a6a',
    textDecoration: 'none',
    border: '1px solid #6b4622',
    borderRadius: '7px',
    padding: '9px 13px',
    transition: 'border-color .15s, color .15s',
    '&:hover': { borderColor: '#d94f2b', color: '#fff3e6' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },
  kicker: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: '#9c8168',
  },

  // Board leads. It is the game; the words are the caption to it.
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '16px',
    width: '100%',
    maxWidth: '1120px',
    margin: 'auto',
    alignItems: 'center',
    '@media (min-width: 900px)': {
      gridTemplateColumns: 'minmax(0, 1fr) 380px',
    },
  },
  boardWrap: {
    display: 'flex',
    justifyContent: 'center',
    '& > div': { maxWidth: 'min(94vw, 68vh)', width: '100%' },
  },

  // Pressed plate, per the panel system: walnut outline, lit top, dark bottom.
  panel: {
    background: '#2a1d13',
    border: '1px solid #4a3020',
    borderRadius: '12px',
    padding: '18px 18px 14px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.12), inset 0 -3px 0 rgba(0,0,0,.4)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  eyebrow: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: '#c4a070',
  },
  title: {
    margin: 0,
    fontSize: '25px',
    lineHeight: 1.15,
    fontWeight: 700,
    letterSpacing: '-0.3px',
  },
  body: {
    margin: 0,
    fontSize: '14.5px',
    lineHeight: 1.62,
    color: '#e6d2b4',
  },

  // The round's roster — bids on one scene, everyone's skip on the next — sits
  // in a recess, like every other readout of numbers in the app.
  rosterList: {
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '9px',
    padding: '9px 11px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,.55)',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  rosterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontFamily: MONO,
    fontSize: '12px',
    color: '#9c8168',
  },
  rosterYou: { color: '#e35c36', fontWeight: 700 },
  rosterValue: { fontVariantNumeric: 'tabular-nums' },

  // The room, drawn rather than wired: there is no room to share from a page
  // that talks to no socket. Same recess as the roster, and the plate and mark
  // below are lifted from the Share room plate the sidebar used to carry, so
  // the control a visitor meets in the game is the one they were shown here.
  // That plate is now `RoomStrip`'s copy-invite button; this scene is the last
  // caller of `game.shareRoom`, and `scenes.test.js` holds it to the wording.
  roomCard: {
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '9px',
    padding: '10px 11px 11px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,.55)',
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
  },
  roomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '10px',
  },
  roomLabel: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#c4a070',
  },
  roomValue: {
    fontFamily: MONO,
    fontSize: '12px',
    letterSpacing: '1px',
    color: '#f5e6c8',
  },
  share: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    height: '40px',
    marginTop: '1px',
    borderRadius: '8px',
    background: '#3a2416',
    color: '#f0dcc0',
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.11em',
    textTransform: 'uppercase',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.14)',
      'inset 0 -2px 0 rgba(0,0,0,0.3)',
      '0 3px 0 #1c1108',
    ].join(','),
  },
  // Two offset squares — a link, without an icon font.
  shareMark: {
    position: 'relative',
    width: '13px',
    height: '13px',
    borderRadius: '3px',
    boxShadow: 'inset 0 0 0 1.6px #d94f2b',
    flexShrink: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '7px',
      top: '-4px',
      width: '9px',
      height: '9px',
      borderRadius: '2px',
      background: '#3a2416',
      boxShadow: 'inset 0 0 0 1.6px #d94f2b',
    },
  },
  // Origin-agnostic on purpose: the real link is built from wherever the game
  // is being served, and inventing a domain here would be the one false note.
  shareUrl: {
    fontFamily: MONO,
    fontSize: '11px',
    color: '#8a6e4c',
    textAlign: 'center',
    wordBreak: 'break-all',
  },

  // ── The signature ────────────────────────────────────────────
  // A robot does not ease to a halt; it travels at one speed and stops dead
  // against whatever caught it. So does this marker: `linear`, then nothing.
  // The stations are its walls, and the chip wears the colour of the robot
  // that moves in the scene it is sitting on.
  track: {
    position: 'relative',
    height: '16px',
    margin: '2px 6px 0',
    display: 'flex',
    alignItems: 'center',
  },
  rail: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: '6px',
    borderRadius: '3px',
    background: '#150d07',
    border: '1px solid #3c2718',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,.6)',
  },
  stations: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
  },
  station: {
    width: '2px',
    height: '10px',
    borderRadius: '1px',
    background: '#4a3020',
  },
  chip: {
    position: 'absolute',
    width: '13px',
    height: '13px',
    borderRadius: '4px',
    transform: 'translateX(-50%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.35), 0 1px 3px rgba(0,0,0,.6)',
    transition: 'left .32s linear, background-color .32s linear',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },

  // Outside the scrolling region on purpose — see `main`. This is the whole
  // fix: the way forward cannot be scrolled away, on any screen, at any height.
  controlBar: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    maxWidth: '1120px',
    margin: '0 auto',
    padding: '12px 0 calc(10px + env(safe-area-inset-bottom, 0px))',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    // Full width on a phone; on desktop it holds the caption column's width, so
    // it stays under the panel it used to sit inside.
    width: '100%',
    '@media (min-width: 900px)': {
      width: '380px',
    },
  },
  counter: {
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '1px',
    color: '#9c8168',
    fontVariantNumeric: 'tabular-nums',
    marginRight: 'auto',
  },
  btn: {
    // inline-flex, not the button default: the finishing control is a <span>
    // inside a link, and an inline span ignores min-height entirely.
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '44px',
    padding: '0 14px',
    borderRadius: '9px',
    border: '1px solid #6b4622',
    background: 'none',
    color: '#d99a6a',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    cursor: 'pointer',
    transition: 'border-color .15s, color .15s',
    '&:hover:not(:disabled)': { borderColor: '#d94f2b', color: '#fff3e6' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
    '&:disabled': { opacity: 0.35, cursor: 'default' },
  },
  // The one raised control on the page, so the way forward is never in doubt.
  primaryBtn: {
    background: '#d94f2b',
    borderColor: '#a5391d',
    color: '#fff3e6',
    boxShadow: '0 3px 0 #7d2a12',
    '&:hover:not(:disabled)': { background: '#e35c36', color: '#fff3e6' },
    '&:active:not(:disabled)': { boxShadow: 'inset 0 2px 5px rgba(0,0,0,.55)' },
  },

  // The finishing control. Same plate as Next, so the eye that has been
  // pressing one button all the way through finds the exit in the same place.
  finishLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
});

function HowToPlay() {
  const classes = useStyles();
  const t = useT();
  const [index, setIndex] = useState(0);
  const scene = SCENES[index];
  const isLast = index === SCENES.length - 1;

  // Opening the tutorial is being offered it, whichever door was used — the
  // menu's welcome panel, the How to play plate, or a bookmarked link. Marked
  // on arrival rather than on the last scene: somebody who leaves at scene
  // three has read the offer and answered it, and asking again is nagging.
  useEffect(() => { markIntroSeen(); }, []);

  const playback = useSolutionPlayback({ walls: scene.walls, stepMs: STEP_MS });
  const { play, stop } = playback;

  // Where the scene ends up, so the board holds its answer once the run is
  // over instead of snapping back the moment the last robot lands.
  const finished = useMemo(
    () => buildFrames(scene.robots, scene.walls, scene.moves),
    [scene]
  );
  const endPositions = finished.at(-1)?.positions ?? scene.robots;
  const endTrails = useMemo(() => {
    const drawn = {};
    finished.forEach(({ color, from, to }) => {
      drawn[color] = [...(drawn[color] ?? []), { x: from.x, y: from.y }, { x: to.x, y: to.y }];
    });
    return drawn;
  }, [finished]);

  // Which scene has been set going, rather than a boolean "finished" flag set
  // from an effect. The distinction matters: the hook hands the board back the
  // instant the last move lands, and if the resting state were still being
  // computed a render later, that one render would show the *starting*
  // positions — which framer-motion animates, so the scene visibly ran
  // backwards and then forwards again at the end of every step.
  const [startedScene, setStartedScene] = useState(null);
  const started = startedScene === scene.id;

  // Every scene starts itself. Arriving on one and having to press play would
  // put a step between the reader and the thing they came to watch.
  const replay = () => play(scene.moves, scene.robots);
  useEffect(() => {
    // Deferred a frame on purpose. Starting playback inside the mount commit
    // batches the opening position and the first move into a single render, so
    // the browser never paints the robot where it starts from and the board's
    // animation has no distance to travel — the move simply does not play.
    // One frame later there is a painted position to leave.
    const frame = requestAnimationFrame(() => {
      setStartedScene(scene.id);
      play(scene.moves, scene.robots);
    });
    return () => { cancelAnimationFrame(frame); stop(); };
  }, [scene, play, stop]);

  const goto = (next) => { stop(); setIndex(next); };

  const positions = playback.positions ?? (started ? endPositions : scene.robots);
  const trails = playback.playing ? playback.trails : (started ? endTrails : {});
  // The scene about skipping has no move to take a colour from, so it borrows
  // its mission's — the same board, sitting still.
  const chipColor = scene.moves[0]?.color ?? scene.activeMission?.color;

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <Link className={classes.backLink} to="/">{t('tutorial.backToMenu')}</Link>
        <span className={classes.kicker}>{t('settings.howToPlay')}</span>
        <div className={classes.headerLanguage}><LanguagePicker /></div>
      </div>

      <div className={classes.main}>
      <div className={classes.layout}>
        <div className={classes.boardWrap}>
          <Board
            boardState={positions}
            walls={scene.walls}
            missions={scene.missions}
            activeMission={scene.activeMission}
            robotTrails={trails}
            highlightedTiles={[]}
            selectedRobot={null}
            compact
          />
        </div>

        <div className={classes.panel}>
          <span className={classes.eyebrow}>
            {t('tutorial.step', { index: index + 1, total: SCENES.length })}
          </span>
          <h1 className={classes.title}>{t(scene.titleKey)}</h1>
          <p className={classes.body}>{t(scene.bodyKey)}</p>

          {scene.roster && (
            <div className={classes.rosterList}>
              {scene.roster.map(player => (
                <div
                  key={player.username}
                  className={`${classes.rosterRow} ${player.you ? classes.rosterYou : ''}`}
                >
                  <span>{player.username}</span>
                  <span className={classes.rosterValue}>
                    {player.skipped
                      ? t('tutorial.readyToSkip')
                      : t(player.you ? 'tutorial.bidYou' : 'tutorial.bidOther', {
                        count: player.count,
                      })}
                  </span>
                </div>
              ))}
            </div>
          )}

          {scene.room && (
            <div className={classes.roomCard}>
              <div className={classes.roomRow}>
                <span className={classes.roomLabel}>{t('menu.roomCode')}</span>
                <span className={classes.roomValue}>{scene.room.code}</span>
              </div>
              <div className={classes.roomRow}>
                <span className={classes.roomLabel}>{t('access.title')}</span>
                <span className={classes.roomValue}>
                  {scene.room.locked
                    ? `🔒 ${t('access.password')}`
                    : t('access.public')}
                </span>
              </div>
              <div className={classes.share}>
                <span className={classes.shareMark} aria-hidden="true" />
                <span>{t('game.shareRoom')}</span>
              </div>
              <span className={classes.shareUrl}>…/game/{scene.room.code}</span>
            </div>
          )}

          <div className={classes.track}>
            <span className={classes.rail} />
            <span className={classes.stations}>
              {SCENES.map(s => <span key={s.id} className={classes.station} />)}
            </span>
            <span
              className={classes.chip}
              style={{
                left: `${(index / (SCENES.length - 1)) * 100}%`,
                backgroundColor: chipColor,
              }}
            />
          </div>
        </div>
      </div>
      </div>

      <div className={classes.controlBar}>
        <div className={classes.controls}>
          <span className={classes.counter}>{index + 1} / {SCENES.length}</span>
          {scene.moves.length > 0 && (
            <button className={classes.btn} onClick={replay}>{t('tutorial.replay')}</button>
          )}
          <button
            className={classes.btn}
            onClick={() => goto(index - 1)}
            disabled={index === 0}
          >
            {t('tutorial.back')}
          </button>
          {isLast ? (
            <Link className={classes.finishLink} to="/">
              <span className={`${classes.btn} ${classes.primaryBtn}`}>
                {t('tutorial.startAGame')}
              </span>
            </Link>
          ) : (
            <button
              className={`${classes.btn} ${classes.primaryBtn}`}
              onClick={() => goto(index + 1)}
            >
              {t('tutorial.next')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default HowToPlay;
